/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import React, { FunctionComponent, useMemo, useState } from 'react';
import { getChatStyleOptions } from '../helpers/StyleComposers';
import { IChatCoreProps, IAction, IDispatch } from '../interfaces';
import { CommerceChatStore } from '../helpers/CommerceChatStore';

/**
 * Just a core Chat component
 * @param props
 */

const ChatCore: FunctionComponent<IChatCoreProps> = (props: IChatCoreProps) => {
    const { ReactWebChat } = window.WebChat;

    const [userId, setUserId] = useState('');
    useMemo(() => {
        if (props.provider) {
            const ctx = props.provider();
            if (ctx.UserId) {
                setUserId(typeof ctx.UserId === 'string' ? ctx.UserId : ctx.UserId.value.toString());
            }
        }
    }, [props.provider]);

    const directLine = useMemo(() => {
        return window.WebChat.createDirectLine({ token: props.directLineToken });
    }, [props.directLineToken]);

    const commerceChatStore = useMemo(() => {
        if (directLine && props.provider) {
            return new CommerceChatStore(props.config, props.provider, props.setNewMessage);
        }
        return undefined;
    }, [props.provider, directLine]);

    const store = useMemo(() => {
        return window.WebChat.createStore(
            {},
            ({ dispatch }: { dispatch: IDispatch }) => (next: (action: IAction) => IDispatch) => (action: IAction) => {
                commerceChatStore?.actionHandler({ dispatch, next, action });
            }
        );
    }, [commerceChatStore]);
    const styleOptions = getChatStyleOptions(props.config.config);

    return props.directLineToken !== '' ? (
        <ReactWebChat directLine={directLine} userID={userId} styleOptions={styleOptions} store={store} />
    ) : (
        <p>Please wait while we are connecting.</p>
    );
};

export default ChatCore;
